"use client";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Astro_Strings } from "../Strings";
import styles from "./DailyHoroscope.module.scss";
import {
  ZODIAC_SIGNS,
  ZODIAC_SIGNS_DETAILS,
  toggleList,
  dayList,
} from "../Data";
import {
  AstroHoroscopeResponse,
  AstroPredictionType,
  AstroSunSignResponse,
  colorCardType,
  zodiacDataType,
} from "../Enum";
import DayTabs from "../components/DayTabs";
import CardContainer from "../components/CardContainer";
import ToggleContainer from "../components/ToggleContainer";
import LogoNavBar from "../components/LogoNavBar";
import PageHeaderCard from "../components/PageHeaderCard";
import AstroContainer from "../components/AstroContainer";
import { Images } from "../../../utils/imageRelativePaths";
import TextCard from "../components/TextCard";
import {
  fetchHoroscopePrediction,
  fetchSunSignPrediction,
} from "@/services/astro.service";
import { YearlyHoroscopeData } from "./YearlyHoroscopeData";
import { getFormatDateFromPrediction1 } from "../helper";
import useAppContext from "@/customhooks/useAppContext";
import { AppContext } from "@/context/AppContext";
import { useParams } from "next/navigation";

const DailyHoroscope = ({ type }: { type?: string }) => {
  const { sign } = useParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const context = useAppContext();
  const token = context.auth.token ?? "";
  const [activeLogo, setActiveLogo] = useState<zodiacDataType>(
    ZODIAC_SIGNS?.filter(({ value }) => value === sign)?.[0] ?? ZODIAC_SIGNS[0]
  );
  const [dailyTab, setDailyTab] = useState<number>(
    !type || Number(type) > 2 ? 1 : Number(type) ?? 1
  );

  const [monthlyTab, setMonthlyTab] = useState<number>(type === "4" ? 1 : 0);
  const [sunSignData, setSunSignData] = useState<AstroSunSignResponse>();
  const [horoscopeData, setHoroscopeData] = useState<AstroHoroscopeResponse>();
  const { trtcClient, privateCallSocket, privateCallData } = context ?? {};

  const getDailyType = () => {
    if (dailyTab === 0) return AstroPredictionType.PREVIOUS;
    if (dailyTab === 1) return AstroPredictionType.DAILY;
    if (dailyTab === 2) return AstroPredictionType.NEXT;

    return AstroPredictionType.DAILY;
  };

  const fetchDailyHoroscope = async () => {
    const response = await fetchSunSignPrediction({
      type: getDailyType(),
      zodiacName: activeLogo.value,
      token,
    });
    setSunSignData(response);
  };

  const fetchMonthlyHoroscope = async () => {
    const response = await fetchHoroscopePrediction({
      zodiacName: activeLogo.value,
      token,
    });
    setHoroscopeData(response);
  };

  useEffect(() => {
    void fetchDailyHoroscope();
  }, [dailyTab, token]);

  useEffect(() => {
    void fetchMonthlyHoroscope();
  }, [monthlyTab, token]);

  useEffect(() => {
    if (scrollRef?.current && (Number(type) === 3 || Number(type) === 4)) {
      setTimeout(
        () => scrollRef?.current?.scrollIntoView({ behavior: "smooth" }),
        1000
      );
    }
  }, [scrollRef]);

  const getCardsData = () => {
    const ZodiacDetails: { [key: string]: colorCardType } = {};
    let cardsData: colorCardType[] = [];
    ZODIAC_SIGNS_DETAILS.forEach((element) => {
      if (element["key"]) {
        ZodiacDetails[element["key"]] = element;
      }
    });
    cardsData = Object.keys(sunSignData?.prediction ?? {})?.map((key) => ({
      icon: ZodiacDetails[key].icon,
      title: ZodiacDetails[key].title,
      description: sunSignData?.prediction?.[key] ?? "",
    }));
    return cardsData;
  };

  return (
    <div className={styles.dailyHoroscopeCont}>
      <div className={styles.topWrapper}>
        <LogoNavBar
          activeLogo={activeLogo as zodiacDataType}
          setActiveLogo={setActiveLogo}
        />
      </div>
      <div className={styles.centerWrapper}>
        <DayTabs
          activeIndex={dailyTab}
          setActiveIndex={setDailyTab}
          data={dayList}
        />
        <div className={styles.pageHeader}>
          <PageHeaderCard
            activeLogo={activeLogo as zodiacDataType}
            title={Astro_Strings.DAILY_HOROSCOPE}
            subTitle={getFormatDateFromPrediction1(
              sunSignData?.prediction_date ?? ""
            )?.toDateString()}
          />
        </div>
        <CardContainer data={getCardsData()} layout={"grid"} />
        <AstroContainer />
      </div>
      <div className={styles.bottomWrapper} ref={scrollRef}>
        <ToggleContainer
          activeIndex={monthlyTab}
          setActiveIndex={setMonthlyTab}
        />
        <TextCard
          title={`${activeLogo.name} ${toggleList[monthlyTab]}`}
          bg_image={Images.saturn_card}
          content={"list"}
          subTitle={`${
            monthlyTab === 0 ? horoscopeData?.prediction_month ?? "" : ""
          }  ${new Date()?.getFullYear()}`}
          description={
            monthlyTab === 0
              ? horoscopeData?.prediction ?? []
              : [YearlyHoroscopeData?.[activeLogo?.value]]
          }
        />
      </div>
    </div>
  );
};

export default DailyHoroscope;
